<template>
  <aside id="partner-sidebar">
    <DsTag
      :label="typeLabel"
      :color="typeColor"
    />

    <h2 class="ds-heading-small ds-margin-top-24">
      {{ metadata.partnerName }}
    </h2>

    <div class="ds-display-flex ds-align-items-center ds-margin-top-12">
      <DsIcon
        category="interface"
        name="location"
        class="ds-margin-right-12 metadata-icon"
      />
      <span>{{ metadata.location.name }}</span>
    </div>
    <div
      v-if="accreditationStatus"
      class="ds-display-flex ds-align-items-center ds-margin-top-12"
    >
      <DsIcon
        category="interface"
        name="checkmark-circle"
        class="ds-margin-right-12 metadata-icon"
      />
      <span>{{ accreditationStatus }}</span>
    </div>
    <!-- Temporally remove the language data from sidebar: WEB-5063 -->
    <!-- <div
      v-if="languages"
      class="ds-display-flex ds-align-items-center ds-margin-top-12"
    >
      <DsIcon
        category="team"
        name="partnerships-team"
        class="ds-margin-right-12 metadata-icon"
      />
      <span>{{ languages }}</span>
    </div> -->
    <div class="ds-display-flex ds-align-items-center ds-margin-top-12">
      <DsIcon
        category="arrows"
        name="external-link"
        class="ds-margin-right-12 metadata-icon"
      />
      <a
        rel="noreferrer noopener"
        :href="metadata.url"
        target="_blank"
        >{{ labels.visitPartner }}</a
      >
    </div>

    <div
      v-for="block in blocks"
      :key="block.id"
      class="ds-margin-top-48"
    >
      <div class="ds-display-flex ds-align-items-center ds-margin-bottom-12">
        <DsIcon
          :category="block.icon.category"
          :name="block.icon.name"
          class="ds-margin-right-12"
        />
        <span class="ds-font-weight-semi-bold">{{ block.label }}</span>
      </div>

      <div
        v-for="item in block.items.slice(0, 4)"
        :key="item.id"
      >
        <template v-if="item.link && item.link.url">
          <DsLink
            type="link"
            variant="tertiary"
            color="black"
            :content="item.link.title"
            :url="item.link.url"
          />
        </template>
        <template v-else>
          <div style="padding: 2px">
            {{ item.name }}
          </div>
        </template>
      </div>
      <div
        v-for="item in block.items.slice(4, 99)"
        v-show="expanded[block.id]"
        :key="item.id"
      >
        <template v-if="item.link && item.link.url">
          <DsLink
            type="link"
            variant="tertiary"
            color="black"
            :content="item.link.title"
            :url="item.link.url"
          />
        </template>
        <template v-else>
          <div style="padding: 2px">
            {{ item.name }}
          </div>
        </template>
      </div>
      <button
        v-if="block.items.length > 4"
        type="button"
        class="ds-display-flex ds-align-items-center ds-margin-top-12 ds-button-link ds-button-link--tertiary ds-button-link--black"
        @click="toggleExpand(block.id)"
      >
        <DsIcon
          class="ds-text-icon__icon ds-text-icon__icon--left ds-margin-right-12"
          category="arrows"
          :name="expanded[block.id] ? 'chevron-up-small' : 'chevron-down-small'"
        />

        {{ expanded[block.id] ? labels.showLess : labels.showMore }}
      </button>
    </div>
  </aside>
</template>

<script setup>
import { camelCase } from 'lodash-es'
import { DsIcon, DsLink, DsTag } from 'awds'

const { labels } = useGlobalData()
const { locale } = useI18n()
const store = useRoutesStore()

store.retrieveRoutes()

const props = defineProps({
  metadata: {
    type: Object,
    required: true
  },
  partnerType: {
    type: String,
    default: 'TECH'
  },
  accreditationStatus: {
    type: Array,
    default: []
  }
})
const expanded = ref({})

const typeLabel = computed(() => {
  return labels.value[`${props.partnerType.toLowerCase() || 'tech'}Partner`]
})
const typeColor = computed(() => {
  return props.partnerType.toLowerCase() === 'tech' ? 'blue' : 'green'
})
const accreditationStatus = computed(() => {
  return props.accreditationStatus.length
    ? props.accreditationStatus.map((a) => labels.value[camelCase(a)]).join(', ')
    : ''
})
const languages = computed(() => {
  return props.metadata.languages.map((l) => l.name).join(', ')
})
const blocks = computed(() => {
  const { getInternalUrl } = useRoutes(store.routes, locale)

  return ['availability', 'industries', 'partners', 'products', 'paymentMethods', 'terminals']
    .map((section) => {
      let items
      // Special case for availability
      if (section === 'availability') {
        items = props.metadata.availabilityLabels
      } else {
        items = props.metadata[section]
      }

      if (!items?.length) {
        return null
      }

      return {
        id: section,
        icon: getIconForSection(section),
        label: getLabelForSection(section),
        items: items
          .map((i) => {
            let link = null
            if (i.id && i.link) {
              link = i.link
            } else if (i.id && getInternalUrl(i.id)) {
              link = {
                text: i.name || i,
                title: i.name || i,
                url: getInternalUrl(i.id),
                nuxt: true,
                openInNewWindow: false
              }
            }
            return {
              id: i.id,
              name: i.name || i,
              link
            }
          })
          .sort((a, b) => (typeof a.name?.localeCompare === 'function' ? a.name.localeCompare(b.name) : 1))
      }
    })
    .filter(Boolean)
})

onMounted(() => {
  ;['availability', 'industries', 'partners', 'products', 'paymentMethods', 'terminals'].forEach((block) => {
    expanded.value[block] = false
  })
})

function toggleExpand(block) {
  expanded.value[block] = !expanded.value[block]
}

function getLabelForSection(section) {
  switch (section) {
    case 'availability':
      return labels.value.availableIn
    case 'industries':
      return labels.value.industry
    case 'partners':
      return labels.value.techPartners
    case 'products':
      return labels.value.adyenProduct
    case 'paymentMethods':
      return labels.value.paymentMethods
    case 'terminals':
      return labels.value.terminalsSupported
    default:
      return '-'
  }
}

function getIconForSection(section) {
  switch (section) {
    case 'availability':
      return {
        category: 'various',
        name: 'globe'
      }
    case 'industries':
      return {
        category: 'products-and-features',
        name: 'accounts'
      }
    case 'partners':
      return {
        category: 'products-24',
        name: 'adyen-giving'
      }
    case 'products':
      return {
        category: 'various',
        name: 'scalability'
      }
    case 'paymentMethods':
      return {
        category: 'products-24',
        name: 'payment-method-types'
      }
    case 'terminals':
      return {
        category: 'devices-24',
        name: 'terminal-3'
      }
    default:
      return {
        category: 'various',
        name: 'globe'
      }
  }
}
</script>
<style lang="scss" scoped>
.metadata-icon {
  width: 24px;
}
</style>
